import {
  Box,
  Stack,
  SxProps,
  Typography,
  capitalize,
  useTheme,
} from "@mui/material";
import React from "react";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import Dropdown from "@/components/dropdown/dropdown";
import { GREY, ERROR_PRIMARY } from "@/config/mui/colorPalette";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { InvoiceWithPaymentsDetailsQuery } from "@/graphql/queries/invoice.graphql.types";
import { isCardExpired } from "@/utils";

type StripeCard =
  InvoiceWithPaymentsDetailsQuery["invoiceByPk"]["client"]["stripeData"]["stripeCardsList"][number];

type PaymentCardComponentProps = {
  card: StripeCard;
  onDelete?: () => void;
  onSetDefault?: () => void;
  sx?: SxProps;
};

const PaymentCardComponent = ({
  card,
  onDelete,
  onSetDefault,
  sx,
}: PaymentCardComponentProps) => {
  const { hasFeaturePermission } = useUser();
  const { clientAccessToken } = useClientAccessToken();
  const theme = useTheme();
  const expiredCard = isCardExpired(card.expYear, card.expMonth);

  const dropdownActions = [
    !card.isDefault && { title: "Set as default", action: onSetDefault },
    {
      title: "Remove this card",
      action: onDelete,
    },
  ].filter(Boolean);

  const expiryText = `${expiredCard ? "Expired" : "Expires"} on ${String(
    card.expMonth
  ).padStart(2, "0")}/${card.expYear}`;

  const canManageCreditCards =
    !!clientAccessToken ||
    hasFeaturePermission?.(FeaturePermission.MANAGE_WALLET_CREDIT_CARDS);

  return (
    <Box
      sx={{
        borderRadius: "16px",
        px: 2,
        py: 1,
        border: "1.5px solid",
        borderColor: expiredCard
          ? ERROR_PRIMARY
          : card.isDefault
            ? theme.palette.primary.main
            : GREY[30],
        flexGrow: 1,
        ...sx,
      }}
    >
      <Stack direction="row" spacing={1}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            flexGrow: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            <Typography
              variant="subtitleDefault"
              sx={{
                fontWeight: 700,
              }}
            >
              {capitalize(card.brand)}
            </Typography>
            {card.isDefault && (
              <Typography
                variant="labelSmall"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                }}
              >
                Default card
              </Typography>
            )}
          </Stack>
          {canManageCreditCards && onDelete && dropdownActions.length > 0 && (
            <Dropdown additionalActions={dropdownActions} />
          )}
        </Stack>
      </Stack>
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          color: "text.secondary",
        }}
      >
        <Typography variant="paragraphSmall">{`*${card.last4}`}</Typography>
        <Typography variant="paragraphSmall">•</Typography>
        <Typography
          variant="paragraphSmall"
          sx={{
            color: expiredCard ? ERROR_PRIMARY : "text.secondary",
          }}
        >
          {expiryText}
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentCardComponent;
